'use client';
import React, { useState } from 'react';

interface Cohort {
  id: number;
  courseId: number;
  label: string;
  startDate: string;
  endDate: string;
  depositNPR: number;
  spotsLeft: number;
}

const cohorts: Cohort[] = [
  {
    id: 5,
    courseId: 3,
    label: 'Sep 1 – Sep 30, 2026 (15 spots, 6 taken)',
    startDate: '2026-09-01',
    endDate: '2026-09-30',
    depositNPR: 37500,
    spotsLeft: 9,
  },
  {
    id: 6,
    courseId: 4,
    label: 'Sep 1 – Sep 15, 2026 (10 spots, 4 taken)',
    startDate: '2026-09-01',
    endDate: '2026-09-15',
    depositNPR: 30000,
    spotsLeft: 6,
  },
];

const courses = [
  { id: 3, title: 'Yoga Teacher Training - 200 hrs [with free Singing Bowls basics]' },
  { id: 4, title: 'Buddha Bowls TTC - 100 hrs [with free Yoga basics]' },
];

const termsText = [
  '1. Booking Policy: Our course payments are pre-contracted. 25% of the package fee is a Non-Refundable deposit that is required to secure the spot. Rest of the amount can be paid on arrival.',
  '2. Cancellation Policy: The deposit paid is non-refundable. In the event of any cancellation, the advance amount will still be useful to you for future bookings and/or can be transferred to another person with iYc. One-month advance notice is required to transfer to future learning (valid for 1 year only) or to a person of your choice.',
  '3. If you choose to leave the course in between, the paid amount would be non-refundable. In any circumstances, the deposited fees are non-refundable unless the course is cancelled by IYc.',
  '4. IYc & its counterparts shall not be responsible for any change & alteration in the program due to change of mind, unavoidable circumstances such as landslide, road blockage, flood, snow, political unrest, and cancellation of flight, delay arrival, sickness or accidents.',
  '5. The management reserves the right to terminate/ expel any participants who do not abide by these rules, who are disruptive or abusive to the place, teachers or other participants. (which may include, by way of example, violence, excessive use of bad language, threatening behavior or inappropriate sexual activities). No refund will be offered in this event.',
];

export default function RegistrationSection() {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedCohortId, setSelectedCohortId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'khalti' | 'manual'>('khalti');
  const [showManualInstructions, setShowManualInstructions] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    whatsapp: '',
    country: '',
    notes: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const availableCohorts = cohorts.filter(
    (c) => c.courseId === parseInt(selectedCourseId)
  );

  const selectedCohort = cohorts.find((c) => c.id === parseInt(selectedCohortId));

  const canSubmit = termsAccepted && selectedCourseId && selectedCohortId && formData.name && formData.email && formData.whatsapp && formData.country;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCourseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCourseId(e.target.value);
    setSelectedCohortId('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (paymentMethod === 'manual') {
      setShowManualInstructions(true);
      return;
    }
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1200));
    setSubmitting(false);
    setSubmitted(true);
  };

  return (
    <section id="registration" className="relative scroll-mt-24 bg-cream-dark/20 py-20">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-saffron-dark uppercase font-bold tracking-widest text-xs md:text-sm border-b-2 border-saffron-light pb-1">
            Secure Your Spot
          </span>
          <h2 className="font-serif text-3xl md:text-4xl text-forest font-bold mt-3 mb-4 leading-tight">
            REGISTRATION
          </h2>
          <p className="font-sans text-charcoal/70 text-sm md:text-base leading-relaxed">
            Fill out the form below to register for your selected cohort. The required deposit is calculated from the selected cohort&apos;s settings.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 max-w-6xl mx-auto">
          {/* Left: Terms */}
          <div className="lg:col-span-5 flex flex-col text-left">
            <div className="bg-white/80 p-6 rounded-3xl border border-forest/10 shadow-md">
              <h3 className="font-serif text-lg md:text-xl text-forest font-bold mb-4">
                Booking Policy &amp; Terms
              </h3>
              <div className="overflow-y-auto max-h-[350px] pr-2 border-b border-forest/5 mb-4 pb-2 space-y-3">
                {termsText.map((term, i) => (
                  <p key={i} className="font-sans text-xs md:text-sm text-charcoal/80 leading-relaxed" style={{ textAlign: 'justify' }}>
                    {term}
                  </p>
                ))}
              </div>
              <div className="flex items-start gap-2.5">
                <input
                  id="accept-terms"
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-forest/20 accent-forest"
                />
                <label htmlFor="accept-terms" className="font-sans text-xs md:text-sm text-charcoal/90 select-none">
                  I read and accept the non-refundable deposit terms and cancellation policy listed above. *
                </label>
              </div>
            </div>
          </div>

          {/* Right: Registration Form */}
          <div className="lg:col-span-7">
            <div className="bg-white/80 p-6 md:p-8 rounded-3xl border border-forest/10 shadow-lg text-left">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-600">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <path d="m9 11 3 3L22 4" />
                    </svg>
                  </div>
                  <h4 className="font-serif text-xl font-bold text-forest mb-2">Registration Submitted!</h4>
                  <p className="font-sans text-sm text-charcoal/70 max-w-sm">
                    Thank you for registering. We will contact you via WhatsApp or email to confirm your spot and provide payment details.
                  </p>
                </div>
              ) : showManualInstructions ? (
                <div className="space-y-4">
                  <h4 className="font-serif text-xl font-bold text-forest">Manual Bank Transfer Instructions</h4>
                  <div className="bg-cream-dark/30 rounded-2xl p-5 space-y-2 font-sans text-sm text-charcoal/80">
                    <p>To secure your spot, please pay the 25% non-refundable deposit via bank transfer or by scanning the QR code below:</p>
                    <div className="mt-3 space-y-1">
                      <p><span className="font-semibold">Bank Name:</span> <strong>Himalayan Bank Limited</strong></p>
                      <p><span className="font-semibold">Account Name:</span> <strong>Zen Teacher Training Center Pvt. Ltd.</strong></p>
                      <p><span className="font-semibold">Account Number:</span> <strong>0123456789</strong></p>
                      <p><span className="font-semibold">Branch:</span> <strong>Durbar Marg, Kathmandu, Nepal</strong></p>
                      <p><span className="font-semibold">Swift Code:</span> <strong>HBL44600</strong></p>
                    </div>
                    {selectedCohort && (
                      <p className="mt-3 font-semibold text-forest">
                        Deposit Amount: NPR {selectedCohort.depositNPR.toLocaleString()}
                      </p>
                    )}
                    <p className="mt-3 text-xs text-charcoal/60">
                      Once the payment is completed, please email a screenshot of your payment receipt to{' '}
                      <a href="mailto:YogaBowls.us@gmail.com" className="text-saffron-dark underline">YogaBowls.us@gmail.com</a>{' '}
                      or send it via WhatsApp to the number listed on this website along with your full name.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowManualInstructions(false)}
                    className="font-sans text-sm text-forest/70 hover:text-forest underline"
                  >
                    ← Back to form
                  </button>
                </div>
              ) : (
                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="reg-name" className="block text-xs font-bold uppercase tracking-wider text-forest/70 mb-1.5">Full Name *</label>
                      <input
                        id="reg-name"
                        name="name"
                        type="text"
                        required
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-forest/10 focus:border-forest focus:outline-none bg-cream/10 text-charcoal text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="reg-email" className="block text-xs font-bold uppercase tracking-wider text-forest/70 mb-1.5">Email Address *</label>
                      <input
                        id="reg-email"
                        name="email"
                        type="email"
                        required
                        placeholder="name@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-forest/10 focus:border-forest focus:outline-none bg-cream/10 text-charcoal text-sm"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="reg-whatsapp" className="block text-xs font-bold uppercase tracking-wider text-forest/70 mb-1.5">WhatsApp Number *</label>
                      <input
                        id="reg-whatsapp"
                        name="whatsapp"
                        type="text"
                        required
                        placeholder="+977-9800000000"
                        value={formData.whatsapp}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-forest/10 focus:border-forest focus:outline-none bg-cream/10 text-charcoal text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="reg-country" className="block text-xs font-bold uppercase tracking-wider text-forest/70 mb-1.5">Country of Residence *</label>
                      <input
                        id="reg-country"
                        name="country"
                        type="text"
                        required
                        placeholder="Nepal"
                        value={formData.country}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-forest/10 focus:border-forest focus:outline-none bg-cream/10 text-charcoal text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="reg-course" className="block text-xs font-bold uppercase tracking-wider text-forest/70 mb-1.5">Select Course *</label>
                    <select
                      id="reg-course"
                      required
                      value={selectedCourseId}
                      onChange={handleCourseChange}
                      className="w-full px-4 py-3 rounded-xl border border-forest/10 focus:border-forest focus:outline-none bg-cream/10 text-charcoal text-sm"
                    >
                      <option value="">Choose your course...</option>
                      {courses.map((c) => (
                        <option key={c.id} value={c.id}>{c.title}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="reg-cohort" className="block text-xs font-bold uppercase tracking-wider text-forest/70 mb-1.5">Select Available Dates &amp; Cohort *</label>
                    <select
                      id="reg-cohort"
                      required
                      disabled={!selectedCourseId}
                      value={selectedCohortId}
                      onChange={(e) => setSelectedCohortId(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-forest/10 focus:border-forest focus:outline-none bg-cream/10 text-charcoal text-sm disabled:opacity-50"
                    >
                      <option value="">{selectedCourseId ? 'Select a cohort...' : 'Select a course first...'}</option>
                      {availableCohorts.map((c) => (
                        <option key={c.id} value={c.id}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="reg-notes" className="block text-xs font-bold uppercase tracking-wider text-forest/70 mb-1.5">
                      Special Notes / Dietary Requirements / Injuries
                    </label>
                    <textarea
                      id="reg-notes"
                      name="notes"
                      rows={3}
                      placeholder="Let us know if you have any food allergies, physical injuries or special accommodations requested..."
                      value={formData.notes}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-forest/10 focus:border-forest focus:outline-none bg-cream/10 text-charcoal text-sm resize-none"
                    />
                  </div>

                  {/* Payment Method */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-forest/70 mb-2">
                      Deposit Payment Option *
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <label
                        className={`flex items-start gap-3 p-4 rounded-xl border-2 transition-all cursor-pointer ${
                          paymentMethod === 'khalti' ?'border-forest bg-forest/5' :'border-forest/10 hover:border-forest/30'
                        }`}
                      >
                        <input
                          type="radio"
                          name="payment-method"
                          value="khalti"
                          checked={paymentMethod === 'khalti'}
                          onChange={() => setPaymentMethod('khalti')}
                          className="mt-1 accent-forest"
                        />
                        <div className="text-left">
                          <p className="text-xs font-bold text-forest">Khalti e-Payment Gateway</p>
                          <p className="text-[10px] text-charcoal/60 mt-0.5">Pay instantly via Wallet, Mobile Banking, or ConnectIPS</p>
                        </div>
                      </label>
                      <label
                        className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          paymentMethod === 'manual' ?'border-forest bg-forest/5' :'border-forest/10 hover:border-forest/30'
                        }`}
                      >
                        <input
                          type="radio"
                          name="payment-method"
                          value="manual"
                          checked={paymentMethod === 'manual'}
                          onChange={() => setPaymentMethod('manual')}
                          className="mt-1 accent-forest"
                        />
                        <div className="text-left">
                          <p className="text-xs font-bold text-forest flex items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M10 18v-7" />
                              <path d="M11.119 2.205a2 2 0 0 1 1.762 0l7.84 3.846A.5.5 0 0 1 20.5 7h-17a.5.5 0 0 1-.22-.949z" />
                              <path d="M14 18v-7" /><path d="M18 18v-7" />
                              <path d="M3 22h18" /><path d="M6 18v-7" />
                            </svg>
                            Manual Bank/QR
                          </p>
                          <p className="text-[10px] text-charcoal/60 mt-0.5">Transfer directly to local bank account</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {selectedCohort && (
                    <div className="bg-saffron-light/10 rounded-xl px-4 py-3 font-sans text-sm">
                      <span className="font-bold text-forest">Deposit required: </span>
                      <span className="text-saffron-dark font-extrabold">NPR {selectedCohort.depositNPR.toLocaleString()}</span>
                      <span className="text-charcoal/60 text-xs ml-2">(25% of package fee)</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={!canSubmit || submitting}
                    className="w-full bg-gradient-to-r from-saffron-light to-saffron-dark text-white font-sans font-bold tracking-wider py-4 rounded-xl hover:shadow-lg transition-all flex items-center justify-center gap-2 hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Processing...' : paymentMethod === 'khalti' ? 'Pay Deposit with Khalti' : 'View Bank Transfer Details'}
                    {!submitting && (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                      </svg>
                    )}
                  </button>
                  <p className="text-center text-xs font-medium text-charcoal/60">
                    {!termsAccepted ? 'Accept the booking policy and terms to continue.' : 'Your information is secure and will only be used for registration purposes.'}
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
